import FastImage from "~/ui/FastImage"
import RNFS from "~/ui/fs"
import { CacheDir, DocumentDir } from "./fs"

/** 与清理页 `ClearCacheListItem.id` 对应 */
export const CACHE_CATEGORY_APP = 1
export const CACHE_CATEGORY_IMAGE = 2
export const CACHE_CATEGORY_LOG = 3

type CacheKind = "app" | "image" | "log"

function classifyCacheEntryName(name: string): CacheKind {
  const n = name.toLowerCase()
  const logHints = ["log", "sentry", "crashlytics", "crash"]
  const imageHints = [
    "sdimagecache",
    "sdwebimage",
    "webimage",
    "image_cache",
    "imagecache",
    "fastimage",
    "hackemist",
    "glide",
    "diskcache",
    "okhttp",
    "fresco",
    "picasso",
    "dylanvann",
    "com.github",
  ]
  if (logHints.some((h) => n.includes(h))) return "log"
  if (imageHints.some((h) => n.includes(h))) return "image"
  return "app"
}

function kindToCategoryId(kind: CacheKind): number {
  if (kind === "image") return CACHE_CATEGORY_IMAGE
  if (kind === "log") return CACHE_CATEGORY_LOG
  return CACHE_CATEGORY_APP
}

function mbFromBytes(bytes: number): number {
  if (bytes <= 0) return 0
  return Math.round((bytes / (1024 * 1024)) * 10) / 10
}

async function getPathSize(path: string): Promise<number> {
  if (!(await RNFS.exists(path))) return 0
  try {
    const stat = await RNFS.stat(path)
    if (stat.isFile()) return Number(stat.size || 0)
    if (!stat.isDirectory()) return 0
  } catch {
    return 0
  }
  try {
    let total = 0
    const items = await RNFS.readDir(path)
    for (const item of items) {
      if (item.isFile()) total += Number(item.size || 0)
      else total += await getPathSize(item.path)
    }
    return total
  } catch {
    return 0
  }
}

async function unlinkRecursive(path: string): Promise<void> {
  if (!(await RNFS.exists(path))) return
  try {
    const stat = await RNFS.stat(path)
    if (stat.isFile()) {
      await RNFS.unlink(path)
      return
    }
    if (!stat.isDirectory()) return
  } catch {
    return
  }
  try {
    const items = await RNFS.readDir(path)
    for (const item of items) {
      await unlinkRecursive(item.path)
    }
    await RNFS.unlink(path)
  } catch {
    // 目录非空或系统占用时忽略单项失败
  }
}

export type CacheMeasurement = {
  byId: Record<number, { bytes: number; mb: number }>
  totalBytes: number
  totalMb: number
}

/**
 * 估算各分类占用：根据系统缓存目录一级子目录名称粗略归类；
 * 日志另含 `Documents/logs`（若存在）。
 */
export async function measureAppCaches(): Promise<CacheMeasurement> {
  const byId: CacheMeasurement["byId"] = {
    [CACHE_CATEGORY_APP]: { bytes: 0, mb: 0 },
    [CACHE_CATEGORY_IMAGE]: { bytes: 0, mb: 0 },
    [CACHE_CATEGORY_LOG]: { bytes: 0, mb: 0 },
  }

  try {
    if (await RNFS.exists(CacheDir)) {
      const children = await RNFS.readDir(CacheDir)
      for (const item of children) {
        const id = kindToCategoryId(classifyCacheEntryName(item.name))
        const sz = await getPathSize(item.path)
        byId[id].bytes += sz
      }
    }
  } catch {
    //
  }

  const logDocPath = `${DocumentDir}/logs`
  try {
    if (await RNFS.exists(logDocPath)) {
      const sz = await getPathSize(logDocPath)
      byId[CACHE_CATEGORY_LOG].bytes += sz
    }
  } catch {
    //
  }

  let totalBytes = 0
  for (const id of [CACHE_CATEGORY_APP, CACHE_CATEGORY_IMAGE, CACHE_CATEGORY_LOG]) {
    byId[id].mb = mbFromBytes(byId[id].bytes)
    totalBytes += byId[id].bytes
  }

  return {
    byId,
    totalBytes,
    totalMb: mbFromBytes(totalBytes),
  }
}

/**
 * 按分类清理本地缓存；图片类会调用 FastImage 磁盘/内存清理并删除归类为图片的缓存目录。
 */
export async function clearCacheCategories(ids: number[]): Promise<void> {
  if (ids.length === 0) return
  const want = new Set(ids)

  try {
    if (await RNFS.exists(CacheDir)) {
      const children = await RNFS.readDir(CacheDir)
      for (const item of children) {
        const id = kindToCategoryId(classifyCacheEntryName(item.name))
        if (!want.has(id)) continue
        try {
          await unlinkRecursive(item.path)
        } catch {
          //
        }
      }
    }
  } catch {
    //
  }

  if (want.has(CACHE_CATEGORY_IMAGE)) {
    try {
      await FastImage.clearDiskCache()
      await FastImage.clearMemoryCache()
    } catch {
      //
    }
  }

  if (want.has(CACHE_CATEGORY_LOG)) {
    const logPath = `${DocumentDir}/logs`
    try {
      if (await RNFS.exists(logPath)) await unlinkRecursive(logPath)
    } catch {
      //
    }
  }
}
